import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { BOOKING_URL, NAV_LINKS } from "@/lib/site";
import { EASE } from "@/components/bits";

export default function Navigation() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [pathname]);

  const dark = scrolled || open;

  return (
    <>
      <header
        data-testid="main-navigation"
        className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-500 ${
          dark ? "bg-cream/85 backdrop-blur-xl border-b border-ink/5" : "bg-transparent"
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12 h-20 flex items-center justify-between">
          <Link data-testid="nav-logo" to="/" aria-label="Actus Osteopati – til forsiden" className="flex items-center">
            <img
              src={dark ? "/assets/logo-dark.png" : "/assets/logo-light.png"}
              alt="Actus Osteopati"
              className="h-9 md:h-11 w-auto"
            />
          </Link>

          <nav className="hidden lg:flex items-center gap-8">
            {NAV_LINKS.map((l) => (
              <NavLink
                key={l.to}
                data-testid={`nav-link-${l.to.slice(1)}`}
                to={l.to}
                className={({ isActive }) =>
                  `text-xs tracking-[0.2em] uppercase transition-colors duration-300 ${
                    dark ? "text-ink/70 hover:text-ink" : "text-sand/80 hover:text-sand"
                  } ${isActive ? (dark ? "!text-ink border-b border-ink/40 pb-1" : "!text-sand border-b border-sand/60 pb-1") : ""}`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <a
              data-testid="nav-book-button"
              href={BOOKING_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="ml-2 inline-flex items-center gap-2 rounded-full bg-beige text-ink px-6 py-2.5 text-xs tracking-[0.2em] uppercase font-medium transition-colors duration-300 hover:bg-beige-deep"
            >
              Book time
              <ArrowUpRight size={14} />
            </a>
          </nav>

          <button
            data-testid="mobile-menu-button"
            onClick={() => setOpen(!open)}
            aria-label={open ? "Lukk meny" : "Åpne meny"}
            className={`lg:hidden p-2 transition-colors duration-300 ${dark ? "text-ink" : "text-sand"}`}
          >
            {open ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4, ease: EASE }}
            className="fixed inset-0 z-40 bg-olive flex flex-col justify-center px-8 lg:hidden"
          >
            <nav className="flex flex-col gap-2">
              {NAV_LINKS.map((l, i) => (
                <motion.div
                  key={l.to}
                  initial={{ opacity: 0, y: 24 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 + i * 0.07, duration: 0.6, ease: EASE }}
                >
                  <NavLink
                    data-testid={`mobile-nav-link-${l.to.slice(1)}`}
                    to={l.to}
                    className="font-display font-light uppercase text-sand text-3xl tracking-wide py-3 block border-b border-sand/10"
                  >
                    {l.label}
                  </NavLink>
                </motion.div>
              ))}
              <motion.div
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5, duration: 0.6, ease: EASE }}
                className="pt-8"
              >
                <a
                  data-testid="mobile-nav-book-button"
                  href={BOOKING_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-3 rounded-full bg-beige text-ink px-8 py-4 text-sm tracking-[0.2em] uppercase font-medium"
                >
                  Book time
                  <ArrowUpRight size={16} />
                </a>
              </motion.div>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
